import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade } from "swiper/modules";
import Image from "next/image";
import classNames from "classnames";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import VideoWrapper from "@/components/VideoWrapper";
import Link from "next/link";
import { addWidthParam, addArchivedClass } from "@/lib/utils";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// Tiny gray placeholder – shown while the real image loads
const PLACEHOLDER_SRC =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNlNWU1ZTUiLz48L3N2Zz4=";

interface AuctionCardSliderProps {
  images?: any;
  swiperSettings?: any;
  videos?: any;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
    sort: number;
  }>;
  linkTo?: string | false;
  noZoom?: boolean;
  imageClassName?: string;
  includeVideos?: boolean;
  priority?: boolean;
  isArchived?: boolean;
}

const Icon: React.FC<{ direction: string }> = ({ direction }) => {
  const iconClass = classNames({
    "rotate-0": direction === "prev",
    "rotate-180": direction === "next",
  });

  return (
    <svg
      width="40"
      height="41"
      viewBox="0 0 40 41"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClass}
    >
      <rect
        width="40"
        height="40"
        rx="8"
        transform="matrix(-1 0 0 1 40 0.5)"
        fill="white"
      />
      <mask
        id="mask0_947_7488"
        maskUnits="userSpaceOnUse"
        x="8"
        y="8"
        width="24"
        height="25"
      >
        <rect
          width="24"
          height="24"
          transform="matrix(-1 0 0 1 32 8.5)"
          fill="#D9D9D9"
        />
      </mask>
      <g mask="url(#mask0_947_7488)">
        <path
          d="M22.6 26.5L24 25.1L19.4 20.5L24 15.9L22.6 14.5L16.6 20.5L22.6 26.5Z"
          fill="#2D2D32"
        />
      </g>
    </svg>
  );
};

const AuctionCardSlider: React.FC<AuctionCardSliderProps> = ({
  images,
  videos,
  media,
  linkTo = false,
  noZoom = false,
  imageClassName = "",
  swiperSettings,
  includeVideos = false,
  priority = false,
  isArchived = false,
}) => {
  const sliderRef = useRef<any>(null);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const [imageIndex, setImageIndex] = useState(0);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [imageIndexLightbox, setImageIndexLightbox] = useState(0);
  // Track which slide indices have been activated so we only load their images
  const [loadedIndices, setLoadedIndices] = useState<Set<number>>(new Set([0, 1]));

  const [dynamicSwiperSettings, setDynamicSwiperSettings] = useState<any>({
    autoplay: true,
    delay: 3000,
  });

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;

    sliderRef.current.swiper.slidePrev();

    setImageIndex(sliderRef?.current?.swiper?.activeIndex);
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;

    sliderRef.current.swiper.slideNext();

    setImageIndex(sliderRef?.current?.swiper?.activeIndex);
  }, []);

  // Use media array if available, otherwise fall back to separate images/videos
  const mediaItems = useMemo(() => {
    if (media && media.length > 0) {
      // Filter out videos if includeVideos is false
      return includeVideos 
        ? media 
        : media.filter(item => item.type === 'image');
    }
    // Fallback to old format
    const imageItems = images?.map((url: string) => ({
      type: 'image' as const,
      url,
      sort: 0,
    })) || [];
    const videoItems = includeVideos 
      ? (videos?.map((url: string) => ({
          type: 'video' as const,
          url,
          sort: 0,
        })) || [])
      : [];
    return [...videoItems, ...imageItems];
  }, [media, images, videos, includeVideos]);

  const totalItems = mediaItems?.length || 0;

  const defaultSwiperSettings = {
    slidesPerView: 1,
    speed: 1500,
    loop: true,
    effect: "fade",
    navigation: false,
    pagination: {
      dynamicBullets: false,
      enabled: false,
    },
    ref: sliderRef,
    className: "h-full cursor-pointer",
    modules: [Pagination, Autoplay, EffectFade],
    onSlideChange: () => {
      const active = sliderRef?.current?.swiper?.activeIndex ?? 0;
      setImageIndex(active);
      setLoadedIndices((prev) => {
        const next = new Set(prev);
        next.add(active);
        next.add(active + 1);
        return next;
      });
      // Pause all videos when slide changes
      Object.values(videoRefs.current).forEach((video) => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    },
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    fadeEffect: { crossFade: true },
    // autoplay: dynamicSwiperSettings.autoplay
    //   ? {
    //       delay: dynamicSwiperSettings.delay,
    //       disableOnInteraction: false,
    //       pauseOnMouseEnter: false,
    //     }
    //   : undefined,
  };

  const imageClassNameProps = classNames(
    "relative h-full w-full rounded-t-lg rounded-b-none object-cover overflow-hidden hover:scale-115 transition-all duration-1000 cursor-pointer select-none",
    imageClassName
  );

  const largeImages =
    images?.map((image: any) => ({
      src: addWidthParam(image, 1600),
    })) || [];

  const largeVideos =
    videos?.map((video: any) => ({
      type: "video",
      src: video,
    })) || [];

  // Create slides for lightbox from media array
  const slides = useMemo(() => {
    if (media && media.length > 0) {
      const filteredMedia = includeVideos
        ? media
        : media.filter(item => item.type === 'image');

      return filteredMedia.map((item) => {
        if (item.type === 'video') {
          return {
            type: "video",
            sources: [
              {
                src: item.url,
                type: "video/mp4",
              },
            ],
          };
        }
        const imageUrl = addWidthParam(item.url, 1600);
        return {
          src: isArchived ? addArchivedClass(item.url, 1600, "cover") : imageUrl,
        };
      });
    }
    // Fallback to old format - apply archive watermark to largeImages if archived
    const processedLargeImages = isArchived
      ? largeImages.map((img: any) => {
          // Extract the URL without width param to recalculate with archived class
          const baseUrl = img.src.split("?")[0];
          return {
            ...img,
            src: addArchivedClass(baseUrl, 1600, "cover"),
          };
        })
      : largeImages;
    const filteredVideos = includeVideos ? largeVideos : [];
    return [...filteredVideos, ...processedLargeImages];
  }, [media, largeVideos, largeImages, includeVideos, isArchived]);

  const memoizedLightbox = useMemo(
    () => (
      <Lightbox
        className={isArchived ? "archived-lightbox" : undefined}
        styles={{
          container: { backgroundColor: "rgba(255, 255, 255, .95)" },
          root: {
            "--yarl__color_button": "#2D2D32",
            "--yarl__color_button_disabled": "#dfdfdf",
            "--yarl__color_button_active": "#0a5599",
            "--yarl__thumbnails_thumbnail_border_color": "transparent",
            "--yarl__thumbnails_thumbnail_active_border_color": "#397ab6",
            "--yarl__button_filter": "none",
            "--yarl__thumbnails_container_background_color": "#0a5599",
            "--yarl__thumbnails_thumbnail_background": "transparent",
            "--yarl__color_backdrop": "none",
            "--yarl__slide_icon_loading_color": "#397ab6",
          },
        }}
        index={imageIndexLightbox}
        plugins={[Zoom, Thumbnails, Video]}
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={slides}
      />
    ),
    [openLightbox, slides]
  );

  return (
    <div
      className="relative h-full group"
      // onMouseEnter={() =>
      //   setDynamicSwiperSettings({ autoplay: true, delay: 2000 })
      // }
      // onMouseLeave={() => setDynamicSwiperSettings({ autoplay: false })}
    >
      {memoizedLightbox}
      <Swiper {...defaultSwiperSettings} {...swiperSettings}>
        {mediaItems &&
          mediaItems.map((item: any, index: number) => (
            <SwiperSlide
              onClick={(e) => {
                // Open lightbox for both images and videos (unless noZoom is true)
                if (!noZoom) {
                  setImageIndexLightbox(index);
                  setOpenLightbox(true);
                }
              }}
              key={index}
            >
              {item.type === 'video' ? (
                <div className="h-full">
                  <VideoWrapper 
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    src={item.url} 
                    className="h-full"
                  />
                </div>
              ) : (
                <Link href={linkTo || ""}>
                  <Image
                    className={imageClassNameProps}
                    src={loadedIndices.has(index) ? isArchived ? addArchivedClass(item.url, 600, "cover") : addWidthParam(item.url, 600) : PLACEHOLDER_SRC}
                    alt="Slider Image"
                    height={500}
                    width={400}
                    sizes="(max-width: 768px) 100vw, 400px"
                    priority={priority && index === 0}
                    loading={priority && index === 0 ? "eager" : "lazy"}
                    placeholder="blur"
                    blurDataURL={PLACEHOLDER_SRC}
                  />
                  <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full overflow-hidden bg-[hsl(0,0%,98.4%,0.2)] bg-fixed opacity-0 transition duration-300 ease-in-out hover:opacity-100" />
                </Link>
              )}
            </SwiperSlide>
          ))}
      </Swiper>
      {totalItems > 0 && imageIndex !== 0 && (
        <div
          className="hidden md:group-hover:block prev-arrow absolute z-10 -bottom-[30px] md:bottom-auto md:top-[50%] left-[51px] md:left-0 -translate-y-1/2 cursor-pointer [&>svg:hover]:-translate-x-[5px] duration-300 transition ml-3"
          onClick={handlePrev}
        >
          <Icon direction="prev" />
        </div>
      )}
      {totalItems > 0 && imageIndex < totalItems - 1 && (
        <div
          className="hidden md:group-hover:block next-arrow absolute z-10 -bottom-[30px] md:bottom-auto md:top-[50%] right-[51px] md:right-0 -translate-y-1/2 cursor-pointer [&>svg:hover]:translate-x-[5px] duration-300 transition mr-3"
          onClick={handleNext}
        >
          <Icon direction="next" />
        </div>
      )}
    </div>
  );
};

export default AuctionCardSlider;
